import { z } from "zod";
import { imageToBase64 } from "../utils/imageToBase64";
import { extractLocationData } from "../utils/location";
import { MAX_IMAGE_LONG_SIDE, MAX_IMAGE_SHORT_SIDE } from "../constants/imageProcessing";
import { resizeImage } from "../utils/resizeImage";
import { requestBackend } from "./client";

export interface ImageDescription {
  title: string;
  caption: string;
  description: string;
  sign_translation?: string;
  location_name?: string;
  date?: string;
  latitude?: number;
  longitude?: number;
  camera?: string;
  lens?: string;
}

function imageDescriptionFromResponse(response: ImageDescriptionResponse): ImageDescription {
  const description = {
    title: response.section_title,
    caption: response.photo_caption,
    description: response.description,
    sign_translation: response.sign_translation,
    location_name: response.location_name,
  };
  return description;
}

const ImageDescriptionResponseSchema = z.object({
  section_title: z.string().max(200).describe("Section title in plain text"),
  photo_caption: z.string().max(20_000).describe("Caption in markdown"),
  description: z.string().max(20_000).describe("Description in markdown"),
  sign_translation: z.string().optional().describe("Translation of any text visible in the image"),
  location_name: z.string().optional().describe("Location where the photo was taken"),
});

type ImageDescriptionResponse = z.infer<typeof ImageDescriptionResponseSchema>;

export async function generatePhotoDescription(
  imageFile: File,
  otherDescriptions: ImageDescription[] = [],
  userNotes?: string,
  signal?: AbortSignal,
): Promise<ImageDescription> {
  const photoMetadata = await extractLocationData(imageFile);
  const resizedImage = await resizeImage(imageFile, MAX_IMAGE_LONG_SIDE, MAX_IMAGE_SHORT_SIDE);
  const base64Image = await imageToBase64(resizedImage);

  const photoMetadataForRequest = {
    location_name: photoMetadata?.locationName,
    date: photoMetadata?.date,
  };
  const body = {
    image_url: `data:image/jpeg;base64,${base64Image}`,
    user_notes: userNotes?.trim() || undefined,
    photo_metadata:
      photoMetadataForRequest.location_name || photoMetadataForRequest.date
        ? photoMetadataForRequest
        : undefined,
    previous_sections:
      otherDescriptions.length > 0
        ? otherDescriptions.map((description) => ({
            title: description.title,
            caption: description.caption || undefined,
            description: description.description,
            location_name: description.location_name,
            date: description.date,
          }))
        : undefined,
  };
  const response = await requestBackend("/photoDescription", {
    body,
    responseSchema: ImageDescriptionResponseSchema,
    signal,
    cacheNamespace: "image-description-v1",
  });

  return {
    ...imageDescriptionFromResponse(response),
    location_name: photoMetadata?.locationName ?? response.location_name,
    date: photoMetadata?.date,
    latitude: photoMetadata?.latitude,
    longitude: photoMetadata?.longitude,
    camera: photoMetadata?.camera,
    lens: photoMetadata?.lens,
  };
}
